/**
 * Activity (see the worker's household/activity.ts): the last few days of what everyone else
 * did. Fetched when the app opens, when it comes back to the foreground, and every couple of
 * minutes while open; "unread" = newer than when you last opened Activity.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react'
import { activityLevels, type ActivityCat } from '@terrazzo/core'
import { useMySettings } from '../store/hooks'
import { store } from '../store/store'

export interface ActivityEntry {
  id: number
  item: string
  actor: string
  at: number
  kind: string
  cat: ActivityCat
  summary: string
}

let entries: ActivityEntry[] | null = null
let loading: Promise<void> | null = null
let users = 0
const subs = new Set<() => void>()

export function refreshActivity(): Promise<void> {
  loading ??= (async () => {
    try {
      const res = await fetch('/api/activity', { credentials: 'same-origin' })
      if (res.ok) {
        entries = (await res.json()) as ActivityEntry[]
        for (const f of subs) f()
      }
    } catch {
      // Offline: keep what we had.
    } finally {
      loading = null
    }
  })()
  return loading
}

function subscribe(cb: () => void) {
  subs.add(cb)
  if (++users === 1) {
    void refreshActivity()
    const onVisible = () => document.visibilityState === 'visible' && void refreshActivity()
    document.addEventListener('visibilitychange', onVisible)
    const t = setInterval(() => document.visibilityState === 'visible' && void refreshActivity(), 2 * 60_000)
    stop = () => {
      document.removeEventListener('visibilitychange', onVisible)
      clearInterval(t)
    }
  }
  return () => {
    subs.delete(cb)
    if (--users === 0) stop?.()
  }
}
let stop: (() => void) | null = null

/** Everything from the last few days (null until the first fetch), newest first. */
export function useActivityFeed(): ActivityEntry[] | null {
  return useSyncExternalStore(subscribe, () => entries)
}

/** What a person sees: not the kinds they turned off, and no "added" for things that are gone. */
export function visibleEntries(list: ActivityEntry[], settings: Record<string, unknown>): ActivityEntry[] {
  const levels = activityLevels(settings)
  return list.filter((e) => levels[e.cat] !== 'off' && (e.cat !== 'added' || store.get(e.item)))
}

/** How many entries arrived since you last opened Activity. */
export function useUnreadActivity(): number {
  const list = useActivityFeed()
  const mine = useMySettings()
  return useMemo(() => {
    const seen = Number(mine.activitySeenAt) || 0
    return list ? visibleEntries(list, mine).filter((e) => e.at > seen).length : 0
  }, [list, mine])
}

/** Keep the feed fresh while mounted (e.g. on the Activity page). */
export function useActivityRefresh() {
  useEffect(() => void refreshActivity(), [])
}
